/**
 * Shared Sentry configuration for both client and server
 */
export const SENTRY_DSN =
	'https://301f36138b8fb5eadb575346186d3219@o968153.ingest.us.sentry.io/4507946504421376';

export const IGNORE_ERRORS = [
	/The email or password you have entered is incorrect/i,
	/Sorry, an unknown error occurred\. Please try again later\./i,
];

/** Patterns used to fingerprint offline / transient fetch failures */
const NETWORK_ERROR_PATTERNS = [
	/fetch-failed: network-failed-to-fetch/i,
	/Failed to fetch/i,
	/load failed/i,
];

const MODULE_ERROR_PATTERNS = [/Importing a module script failed/i];

/**
 * @param {unknown} message - Exception message or serialized error text
 * @returns {boolean}
 */
function isNetworkErrorMessage(message) {
	const text = message == null ? '' : String(message);
	return NETWORK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * @param {unknown} message - Exception message or serialized error text
 * @returns {boolean}
 */
function isModuleImportErrorMessage(message) {
	const text = message == null ? '' : String(message);
	return MODULE_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * @param {unknown} error -  string, Error-like, or other
 * @returns {string} Text representation of error
 */
function stringyError(error) {
	if (error == null) {
		return '';
	}
	if (typeof error === 'string') {
		return error;
	}
	if (typeof error === 'object' && 'message' in error) {
		const msg = /** @type {{ message?: unknown }} */ (error).message;
		return msg == null ? '' : String(msg);
	}
	return String(error);
}

/**
 * Whether this event represents a network-layer fetch failure we fingerprint as a group.
 * Checks exception messages and `event.extra.error`.
 * @param {any} event - Sentry event
 * @returns {boolean}
 */
function hasNetworkError(event) {
	const values = event.exception?.values;
	if (Array.isArray(values)) {
		if (values.some((ex) => isNetworkErrorMessage(ex?.value))) {
			return true;
		}
	}

	const errorText = stringyError(event.extra?.error);
	return errorText !== '' && isNetworkErrorMessage(errorText);
}

/**
 * Dynamic import / module script failures (e.g. during navigation).
 * Checks exception messages and `event.extra.error`.
 * @param {any} event - Sentry event
 * @returns {boolean}
 */
function hasModuleImportError(event) {
	const values = event.exception?.values;
	if (Array.isArray(values)) {
		if (values.some((ex) => isModuleImportErrorMessage(ex?.value))) {
			return true;
		}
	}

	const errorText = stringyError(event.extra?.error);
	return errorText !== '' && isModuleImportErrorMessage(errorText);
}

/**
 * Shared beforeSend hook for filtering events
 * @param {any} event - Sentry event
 * @returns {any|null} - Event to send or null to drop
 */
export function beforeSendHandler(event) {
	if (event.exception && Array.isArray(event.exception.values)) {
		const hasExplicitlyFalseFatal = event.exception.values.some(
			/**@param {{ value: string, fatal: boolean }} ex - Sentry exception value
			 * @returns {boolean} - True if the exception has a fatal property and it is false
			 */
			(ex) => 'fatal' in ex && ex.fatal === false
		);

		if (hasExplicitlyFalseFatal) {
			return null;
		}
	}

	if (hasNetworkError(event)) {
		event.fingerprint = ['network-error', 'fetch-failed', 'ignore-network-error'];

		const errorData = event.extra || {};
		if (errorData.offline === true) {
			event.tags = { ...event.tags, network_status: 'offline' };
		}
	}

	if (hasModuleImportError(event)) {
		event.fingerprint = ['module-error', 'unloading', 'ignore-module-error'];
	}

	if (
		event.fingerprint &&
		event.fingerprint.some(
			/**@param {string} f - Sentry fingerprint value
			 * @returns {boolean} - True if the fingerprint value includes 'ignore'
			 */
			(f) => f.includes('ignore')
		)
	) {
		return null;
	}

	return event;
}

/**
 * Get base Sentry configuration (common to client and server)
 * @param {object} options - Configuration options
 * @param {string} options.environment - Environment name
 * @param {string} options.release - Release name
 * @param {number} [options.tracesSampleRate] - Traces sample rate (optional, defaults to 0.05 for prod, 1 otherwise)
 * @returns {object} - Sentry init configuration
 */
export function getSentryConfig({ environment, release, tracesSampleRate }) {
	const defaultSampleRate = environment === 'production' ? 0.05 : 1;

	return {
		dsn: SENTRY_DSN,
		tracesSampleRate: tracesSampleRate ?? defaultSampleRate,
		environment,
		release,
		normalizeDepth: 6,
		attachStacktrace: true,
		ignoreErrors: IGNORE_ERRORS,
		beforeSend: beforeSendHandler,
	};
}
